import { type ReactNode } from "react";
import { Upload, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface UploadPlaceholderProps {
  src: string | null;
  label: string;
  title?: string;
  alt?: string;
  icon?: LucideIcon;
  isDragOver?: boolean;
  onClick?: () => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  children?: ReactNode;
  className?: string;
}

export function UploadPlaceholder({
  src,
  label,
  title,
  alt = "Upload preview",
  icon: Icon = Upload,
  isDragOver,
  onClick,
  onDrop,
  onDragOver,
  onDragLeave,
  children,
  className,
}: UploadPlaceholderProps) {
  const box = (
    <div
      className={cn(
        "relative rounded-2xl overflow-hidden bg-card cursor-pointer transition-all duration-500 max-w-[226px] w-full aspect-square shadow-[0_0_30px_hsl(var(--brand)/0.06)]",
        src
          ? "border border-brand/30 shadow-[0_0_20px_hsl(var(--brand)/0.08)]"
          : cn(
              "border-dashed border-2",
              isDragOver
                ? "border-brand shadow-[0_0_40px_hsl(var(--brand)/0.15)] bg-brand/5"
                : "border-brand/30 hover:border-brand/60 hover:shadow-[0_0_30px_hsl(var(--brand)/0.1)]",
            ),
        className,
      )}
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {src ? (
        <>
          <img src={src} alt={alt} className="w-full h-full object-cover" />
          {children}
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-4">
          <div className="w-10 h-10 bg-brand/10 rounded-xl flex items-center justify-center mb-2 border border-brand/30">
            <Icon className="w-5 h-5 text-brand" />
          </div>
          <span className="text-xs font-semibold text-muted-foreground/80 uppercase tracking-wider mb-1">
            {label}
          </span>
        </div>
      )}
    </div>
  );

  if (title) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-foreground font-bold text-base md:text-lg">
          {title}
        </h3>
        {box}
      </div>
    );
  }

  return box;
}
